import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { RTL, LANGS, HREFLANG, setCurrentLang } from "../i18n";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("suite_token"));
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("suite_support_session") || "");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem("suite_theme") || "light");
  const [bgColor, setBgColor] = useState(() => localStorage.getItem("suite_bg") || "");
  const [lang, setLang] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("lang");
      const code = q && q.toUpperCase();
      if (code && LANGS.some((l) => l.code === code)) { localStorage.setItem("suite_lang", code); return code; }
    } catch { /* noop */ }
    return localStorage.getItem("suite_lang") || "ENG";
  });
  setCurrentLang(lang);
  const [hadUrlLang] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("lang");
      return !!(q && LANGS.some((l) => l.code === q.toUpperCase()));
    } catch { return false; }
  });
  const [unlocked, setUnlocked] = useState([]);
  const [secProfile, setSecProfile] = useState({ cluster_method: "off", fopfo_method: "off", cluster_pin_set: false, fopfo_pin_set: false });
  const [caps, setCaps] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("suite_caps") || "{}"); } catch { return {}; }
  });

  const loadSecProfile = useCallback(() => {
    client.get("/security/profile").then((r) => setSecProfile(r.data)).catch(() => {});
  }, []);

  const unlockCap = useCallback(async (scope, creds) => {
    const { data } = await client.post("/security/step-up", { scope, ...creds });
    setCaps((c) => {
      const next = { ...c, [scope]: data.token };
      sessionStorage.setItem("suite_caps", JSON.stringify(next));
      return next;
    });
    return data;
  }, []);

  const refreshAccess = useCallback(() => {
    client.get("/integration/access").then((r) => setUnlocked(r.data.unlocked || [])).catch(() => {});
  }, []);

  // apply theme + bg + dir
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    localStorage.setItem("suite_theme", theme);
  }, [theme]);

  useEffect(() => {
    const fallback = theme === "dark" ? "#0A0A0A" : "#F4F4F4";
    document.documentElement.style.setProperty("--app-bg", bgColor || fallback);
    localStorage.setItem("suite_bg", bgColor);
  }, [bgColor, theme]);

  useEffect(() => {
    document.documentElement.setAttribute("dir", RTL.includes(lang) ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", HREFLANG[lang] || "en");
    localStorage.setItem("suite_lang", lang);
  }, [lang]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    client.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("suite_demo", r.data && r.data.demo ? "1" : "");
        const pref = r.data && r.data.language_preference;
        if (!hadUrlLang && pref && LANGS.some((l) => l.code === pref)) setLang(pref);
      })
      .catch(() => { localStorage.removeItem("suite_token"); setToken(null); })
      .finally(() => setLoading(false));
    refreshAccess();
    loadSecProfile();
  }, [token, refreshAccess, loadSecProfile, hadUrlLang]);

  const login = useCallback((tok, usr) => {
    localStorage.setItem("suite_token", tok);
    localStorage.setItem("suite_demo", usr && usr.demo ? "1" : "");
    setToken(tok); setUser(usr);
  }, []);

  const loginSupport = useCallback((tok, usr, sid) => {
    localStorage.setItem("suite_token", tok);
    localStorage.setItem("suite_support_session", sid || "");
    setSessionId(sid || ""); setToken(tok); setUser(usr);
  }, []);

  const refreshUser = useCallback(() => {
    return client.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
  }, []);

  const logout = useCallback(() => {
    if (sessionId) { client.post("/auth/support-logout", { session_id: sessionId }).catch(() => {}); }
    localStorage.removeItem("suite_token");
    localStorage.removeItem("suite_support_session");
    localStorage.removeItem("suite_demo");
    sessionStorage.removeItem("suite_caps");
    setSessionId(""); setToken(null); setUser(null); setCaps({});
  }, [sessionId]);

  // Keep support seat alive while the staffer is active
  useEffect(() => {
    if (user?.role !== "support" || !sessionId) return;
    const ping = () => client.post("/support/heartbeat", { session_id: sessionId }).catch(() => {});
    ping();
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, [user, sessionId]);

  // Global staff presence heartbeat (admin + support): keeps last_seen fresh for the
  // "on shift by office & language" board, regardless of which page is open. Runners excluded.
  useEffect(() => {
    if (!user || user.role === "user") return;
    const ping = () => client.post("/presence/ping", {}).catch(() => {});
    ping();
    const id = setInterval(ping, 60000);
    return () => clearInterval(id);
  }, [user]);

  const isSupport = user?.role === "support";
  const perms = user?.permissions || {};
  const can = (p) => user?.role === "admin" || (isSupport && !!perms[p]);

  // Choose UI language: updates the app immediately + persists to the account
  // (functional profile setting, not a cookie) when signed in, so the choice follows
  // the Runner across devices. Also fires an anonymous analytics beacon so admins
  // can see which languages visitors actually reach for.
  const chooseLang = useCallback((code) => {
    const prev = (typeof localStorage !== "undefined" && localStorage.getItem("suite_lang")) || "ENG";
    setLang(code);
    if (localStorage.getItem("suite_token")) {
      client.put("/me/language", { language: code }).catch(() => {});
    }
    if (code && code !== prev) {
      const vid = (() => {
        try {
          let v = localStorage.getItem("suite_visitor_id");
          if (!v) {
            v = Math.random().toString(36).slice(2) + Date.now().toString(36);
            localStorage.setItem("suite_visitor_id", v);
          }
          return v;
        } catch { return "anon"; }
      })();
      client.post("/analytics/lang-select", {
        to: code, frm: prev,
        path: (typeof window !== "undefined" ? window.location.pathname : "/"),
        visitor_id: vid,
      }).catch(() => {});
    }
  }, []);

  return (
    <AppCtx.Provider value={{ token, user, loading, login, loginSupport, logout, theme, setTheme, bgColor, setBgColor, lang, setLang, chooseLang, unlocked, refreshAccess, refreshUser, isUnlocked: (a) => unlocked.includes(a), isAdmin: user?.role === "admin", isSupport, perms, can, delegatedKeys: user?.delegated_keys || [], delegationExpiresAt: user?.delegation_expires_at || null, manager: user?.manager || null, isManager: !!user?.manager, isSenior: !!user?.is_senior, sessionId, mustChangePassword: !!user?.must_change_password, needsAgreement: !!user?.needs_agreement,
      secProfile, loadSecProfile, caps, unlockCap,
      capMethod: (scope) => secProfile[`${scope}_method`] || "off",
      capRequired: (scope) => (secProfile[`${scope}_method`] || "off") !== "off",
      hasCap: (scope) => !!caps[scope],
      capOk: (scope) => (secProfile[`${scope}_method`] || "off") === "off" || !!caps[scope] }}>
      {children}
    </AppCtx.Provider>
  );
}
